import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../ui/Toast';

// Panel admin « Modèles » (Phase 2) — pilote le catalogue (actif / catégorie / rang)
// et les assignations tâche→modèle SANS redéploiement (effet immédiat via invalidation
// des caches CapabilityRegistry + ModelAssignmentService).
export default function ModelCatalogAdmin() {
  const [tab, setTab] = useState('assignments'); // 'assignments' | 'catalog'
  const [catalog, setCatalog] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectable, setSelectable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('oracle_token')}`,
    'Content-Type': 'application/json',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/models', { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setCatalog(data.catalog || []);
      setAssignments(data.assignments || []);
      setSelectable(data.selectable || []);
    } catch (e) {
      toast.error(`Chargement modèles : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAssignment = async (task_key, model_id) => {
    setSavingId(task_key);
    try {
      const res = await fetch('/api/admin/models/assignments', {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ assignments: [{ task_key, model_id }] }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAssignments(prev => prev.map(a => a.task_key === task_key ? { ...a, model_id } : a));
      toast.success('Assignation mise à jour (effet immédiat)');
    } catch (e) {
      toast.error(`Échec : ${e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const patchModel = async (model_id, patch) => {
    setSavingId(model_id);
    try {
      const res = await fetch(`/api/admin/models/catalog/${encodeURIComponent(model_id)}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setCatalog(prev => prev.map(m => m.model_id === model_id ? { ...m, ...data.model } : m));
      toast.success('Catalogue mis à jour');
      load(); // re-tire selectable (un modèle désactivé sort des dropdowns)
    } catch (e) {
      toast.error(`Échec : ${e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)', borderBottom: '1px solid var(--card-border)' };
  const td = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid var(--card-border)', color: 'var(--text)' };
  const tabBtn = (active) => ({
    padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    border: '1px solid var(--card-border)',
    background: active ? '#1D4ED8' : 'var(--card-bg)', color: active ? '#fff' : 'var(--text-secondary)',
  });

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Modèles</h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 18 }}>
        Pilote le catalogue et l'assignation des modèles par tâche — <strong>sans redéploiement</strong>.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <button style={tabBtn(tab === 'assignments')} onClick={() => setTab('assignments')}>Tâches → Modèle</button>
        <button style={tabBtn(tab === 'catalog')} onClick={() => setTab('catalog')}>Catalogue</button>
        <button style={{ ...tabBtn(false), marginLeft: 'auto' }} onClick={load} disabled={loading}>↻ Rafraîchir</button>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', padding: 20 }}>Chargement…</div>
      ) : tab === 'assignments' ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--card-bg)', borderRadius: 10, overflow: 'hidden' }}>
          <thead><tr><th style={th}>Tâche</th><th style={th}>Modèle assigné</th></tr></thead>
          <tbody>
            {assignments.length === 0 && (
              <tr><td style={td} colSpan={2}>Aucune assignation (migration 122 non passée ?).</td></tr>
            )}
            {assignments.map(a => (
              <tr key={a.task_key}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{a.label || a.task_key}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{a.task_key}</div>
                </td>
                <td style={td}>
                  <select
                    value={a.model_id}
                    disabled={savingId === a.task_key}
                    onChange={(e) => saveAssignment(a.task_key, e.target.value)}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 13, minWidth: 240 }}
                  >
                    {!selectable.some(s => s.model_id === a.model_id) && (
                      <option value={a.model_id}>{a.model_id} (inactif)</option>
                    )}
                    {selectable.map(s => (
                      <option key={s.model_id} value={s.model_id}>
                        {s.label}{s.category === 'thinking' ? ' · 🧠' : ''} — {s.model_id}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--card-bg)', borderRadius: 10, overflow: 'hidden' }}>
          <thead><tr>
            <th style={th}>Modèle</th><th style={th}>Provider</th><th style={th}>Tier</th>
            <th style={th}>Catégorie</th><th style={th}>Rang</th><th style={th}>Chat</th><th style={th}>Statut</th>
          </tr></thead>
          <tbody>
            {catalog.map(m => (
              <tr key={m.model_id} style={{ opacity: m.status === 'active' ? 1 : 0.5 }}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{m.model_id}</div>
                </td>
                <td style={td}>{m.provider}</td>
                <td style={td}>{m.tier}</td>
                <td style={td}>
                  <input
                    defaultValue={m.category || ''}
                    placeholder="—"
                    disabled={savingId === m.model_id}
                    onBlur={(e) => { const v = e.target.value.trim() || null; if (v !== (m.category || null)) patchModel(m.model_id, { category: v }); }}
                    style={{ width: 90, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 12 }}
                  />
                </td>
                <td style={td}>
                  <input type="number" defaultValue={m.rank ?? ''} placeholder="—"
                    disabled={savingId === m.model_id}
                    onBlur={(e) => { const v = e.target.value === '' ? null : parseInt(e.target.value, 10); if (v !== (m.rank ?? null)) patchModel(m.model_id, { rank: v }); }}
                    style={{ width: 56, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 12 }}
                  />
                </td>
                <td style={td}>
                  <input type="checkbox" checked={!!m.available_in_chat} disabled={savingId === m.model_id}
                    onChange={(e) => patchModel(m.model_id, { available_in_chat: e.target.checked })} />
                </td>
                <td style={td}>
                  <button
                    disabled={savingId === m.model_id}
                    onClick={() => patchModel(m.model_id, { status: m.status === 'active' ? 'disabled' : 'active' })}
                    style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, border: '1px solid var(--card-border)',
                      background: m.status === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.12)',
                      color: m.status === 'active' ? '#16a34a' : 'var(--text-secondary)' }}
                  >
                    {m.status === 'active' ? 'Actif' : (m.status || 'inactif')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
